/* Central timezone/format helpers.
 *
 * Every timestamp shown in the UI must render in IST (Asia/Kolkata, UTC+5:30)
 * regardless of the browser locale. Payloads sent to/from the backend remain
 * ISO UTC -- only the DISPLAY layer changes here.
 *
 * Date format: DD/MM/YYYY (Indian format).
 * Time format: 24-hour HH:mm or HH:mm:ss.
 */

export const IST_TZ = "Asia/Kolkata";

function _toDate(ts) {
  if (ts == null || ts === "") return null;
  if (ts instanceof Date) return isNaN(ts.getTime()) ? null : ts;
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

/** DD/MM/YYYY in IST. Returns "-" if input is falsy or invalid. */
export function formatISTDate(ts) {
  const d = _toDate(ts);
  if (!d) return "-";
  return d.toLocaleDateString("en-GB", { timeZone: IST_TZ, day: "2-digit", month: "2-digit", year: "numeric" });
}

/** HH:mm in IST (24h). Returns "-" if input is falsy or invalid. */
export function formatISTTime(ts) {
  const d = _toDate(ts);
  if (!d) return "-";
  return d.toLocaleTimeString("en-GB", { timeZone: IST_TZ, hour: "2-digit", minute: "2-digit", hour12: false });
}

/** DD/MM/YYYY HH:mm in IST (24h). */
export function formatIST(ts) {
  const d = _toDate(ts);
  if (!d) return "-";
  return `${formatISTDate(d)} ${formatISTTime(d)}`;
}

/** DD/MM/YYYY HH:mm:ss in IST (24h) -- for audit/log tables where seconds matter. */
export function formatISTDateTime(ts) {
  const d = _toDate(ts);
  if (!d) return "-";
  const date = formatISTDate(d);
  const time = d.toLocaleTimeString("en-GB", { timeZone: IST_TZ, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  return `${date} ${time}`;
}

/** "DD Mon" / "DD Mon HH:mm" for compact chips. */
export function formatISTShort(ts, withTime = false) {
  const d = _toDate(ts);
  if (!d) return "-";
  const base = d.toLocaleDateString("en-GB", { timeZone: IST_TZ, day: "numeric", month: "short" });
  return withTime ? `${base} ${formatISTTime(d)}` : base;
}

/** "DD Mon YYYY" -- for detail views. */
export function formatISTLong(ts) {
  const d = _toDate(ts);
  if (!d) return "-";
  return d.toLocaleDateString("en-GB", { timeZone: IST_TZ, day: "2-digit", month: "short", year: "numeric" });
}

/** Month + year label from a "YYYY-MM" month string. Used by MeAttendance. */
export function formatISTMonthLabel(yyyyMm) {
  if (!yyyyMm) return "";
  const d = _toDate(`${yyyyMm}-01T00:00:00Z`);
  if (!d) return yyyyMm;
  return d.toLocaleDateString("en-GB", { timeZone: IST_TZ, month: "long", year: "numeric" });
}

/**
 * Human-friendly relative time ("2h ago", "just now", "in 3d") based on IST.
 * Falls back to formatIST() for anything > 30 days.
 */
export function formatISTRelative(ts) {
  const d = _toDate(ts);
  if (!d) return "-";
  const diff = Date.now() - d.getTime();
  const abs = Math.abs(diff);
  const min = 60_000, hour = 60 * min, day = 24 * hour;
  const sign = diff >= 0 ? "" : "in ";
  const suf = diff >= 0 ? " ago" : "";
  if (abs < 45_000) return "just now";
  if (abs < hour) return `${sign}${Math.round(abs / min)}m${suf}`;
  if (abs < day)  return `${sign}${Math.round(abs / hour)}h${suf}`;
  if (abs < 30 * day) return `${sign}${Math.round(abs / day)}d${suf}`;
  return formatIST(d);
}

/** Handy default export for legacy imports. */
export default formatIST;
